import {
  publicHttpClient,
  type AxiosRequestConfig,
} from '@ahm/api-wrappers-core';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { apiRoutes } from './api-routes';

export interface OrderDetail {
  code: string;
  status: string;
  service_id: string;
  city_id: string;
  items: Item[];
  applied_promo: AppliedPromo[];
  delivery_order_id: string;
  currency: string;
  delivery_partner_id: string;
  subtotal: number;
  total_payment: number;
  store_id: number;
  receiver: Receiver;
  customer_id: string;
  assigning_time: number;
  cancelled_time: number;
  order_time: number;
  confirmed_time: number;
  completed_time: number;
  delivery_discount: number;
  merchant_discount: number;
  platform_discount: number;
  item_discount: number;
  delivery_fee: number;
  platform_fee: number;
  item_fee: number;
  distance: number;
  tracking_link: string;
  delivery_subscription_code: string;
  estimated_total_payment: number;
  branch_address: string;
  branch_id: string;
  branch_mobile: string;
  store_name: string;
  slug: string;
  store_image_url: string;
  rating_summary: RatingSummary;
}

export interface RatingSummary {
  overall: number;
  comment: string;
}

export interface Item {
  id: number;
  price: number;
  customization_total_pay: number;
  quantity: number;
  note: string;
  customizations: ItemCustomization[];
  name: string;
}

export interface ItemCustomization {
  id: string;
  price: number;
  quantity: number;
  option_name: string;
  name: string;
  note: string;
  is_item: boolean;
}

export interface AppliedPromo {
  promo_code: string;
  name: string;
  order_code: string;
  scope: string;
  item_ids: number[];
  store_id: number;
  total_discount_value: number;
  create_at: number;
  discount_config: DiscountConfig;
}

export interface DiscountConfig {
  type: string;
  value: number;
  max_discount_value: number;
  max_apply_per_item: number;
}

export interface Receiver {
  lat: number;
  lng: number;
  address: string;
  name: string;
  mobile: string;
  customer_id: string;
}

export const getOrderDetailFn = async (
  token: string,
  orderCode: string,
  config: AxiosRequestConfig = {}
): Promise<OrderDetail> => {
  const response = await publicHttpClient.get<OrderDetail>(
    apiRoutes.ahafood.store.get_order_detail(orderCode),
    {
      headers: { Authorization: `Bearer ${token}` },
      ...config,
    }
  );
  return response.data;
};

export const useGetOrderDetail = (
  token: string,
  orderCode: string,
  opts?: Omit<UseQueryOptions<OrderDetail, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery<OrderDetail, Error>({
    queryKey: [apiRoutes.ahafood.store.get_order_detail(orderCode)],
    queryFn: () => getOrderDetailFn(token, orderCode),
    ...opts,
  });
};
